import React from 'react';
import { BsTrash } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button,
  Flex,
  HStack,
  Icon,
  IconButton
} from '@chakra-ui/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import PropTypes from 'prop-types';

import TipTapRichTextEditor from '../../../../../../../../../components/TipTapRichTextEditor';
import { getCustomImagePlugin } from '../../../../../../../../../components/TipTapRichTextEditor/CustomImageExtension';
import { patchTipTapHTML } from '../../../../../../../../../components/TipTapRichTextEditor/util';
import { analyticsIds } from '../../../../../../../../../constants/analyticsIds';
import {
  closeAddComment,
  closeSubComment, createActivitySubComment,
  createCommentForRecord, createCommentSubComment, resetTaggedUsersToComment,
  selectElementsToLinkToComment, selectTagUsersToComment,
  unlinkAllEntitiesToComment
} from '../../../../../../../../../redux/modules/activityLog';
import { selectUser } from '../../../../../../../../../redux/modules/user';
import { ContainerDimensions } from '../../../../../../../../../styles/layoutDimensions';
import ActivityLogAtButton from '../ActivityLogAtButton';
import ActivityLogLinkPopover from '../ActivityLogLinkPopover';
import ActivityLogUserIcon from '../ActivityLogUserIcon';
import { parseLinkedElements, parseTaggedUsersToComment } from '../util';

const ActivityLogAddComment = ({ companyId, insuredId, recordId, jobId, activityId, activityType, subComment = false }) => {
  const dispatch = useDispatch();
  const elementsToLinkData = useSelector(selectElementsToLinkToComment);
  const currentUser = useSelector(selectUser);
  const taggedUsers = useSelector(selectTagUsersToComment);

  const editor = useEditor({
    content: null,
    /**
     * This option gives us the control to enable the default behavior of rendering the editor immediately.
     */
    immediatelyRender: true,
    /**
     * This option gives us the control to disable the default behavior of re-rendering the editor on every transaction.
     */
    shouldRerenderOnTransaction: false,
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true }
      }),
      getCustomImagePlugin(dispatch)
    ],
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        style: 'min-height: 100px; padding: 8px; border: 1px solid #ccc; border-radius: 4px;'
      }
      // if we want to enable the enter button to post the comment, uncomment below
      // handleKeyDown: (view, event) => {
      //   if (event.key === 'Enter'
      //     && !event.shiftKey
      //     && !event.ctrlKey
      //     && !event.altKey
      //     && !event.metaKey) {
      //     event.preventDefault(); // prevent adding a line break
      //     handleCommentSubmit();
      //     return true;
      //   }
      //
      //   return false;
      // }
    }
  });

  function handleCommentCancel() {
    if (subComment) {
      dispatch(closeSubComment());
    } else {
      dispatch(closeAddComment());
      dispatch(unlinkAllEntitiesToComment());
      dispatch(resetTaggedUsersToComment());
    }
  }

  function handleCommentSubmit() {
    const value = patchTipTapHTML(editor.getHTML());

    if (subComment) {
      if (activityType !== 'comment') {
        dispatch(createActivitySubComment(value, activityId));
      } else {
        dispatch(createCommentSubComment(value, activityId));
      }

      dispatch(closeSubComment());
    } else {
      dispatch(createCommentForRecord(recordId, value));
      dispatch(closeAddComment());
      dispatch(unlinkAllEntitiesToComment());
    }
  }

  return (
    <Flex
      justifyContent="flex-start"
      maxW={ContainerDimensions.maxWidth}
      ml={6}
      mr={10}
      my={4}
    >
      <Box mr={8}>
        <ActivityLogUserIcon firstName={currentUser.firstName} lastName={currentUser.lastName} />
      </Box>
      <Box flexBasis="100%" textAlign="left">
        <TipTapRichTextEditor editor={editor} />
        { !subComment
          ? (
            <Box
              color="primaryScale.500"
              fontSize="xs"
              textAlign="right"
            >
              {parseLinkedElements(elementsToLinkData)}
            </Box>
          )
          : ''}
        <Box
          color="primaryScale.500"
          fontSize="xs"
          textAlign="right"
        >
          {parseTaggedUsersToComment(taggedUsers)}
        </Box>
        <HStack justifyContent="flex-end" mt={2} spacing={2}>
          <ActivityLogAtButton companyId={companyId} />
          { subComment
            ? ''
            : (
              <ActivityLogLinkPopover insuredId={insuredId} jobId={jobId} recordId={recordId} />
            )}
          <Button
            data-analytics-id={analyticsIds.insuredProfilePage.activityLog.postCommentButton}
            minW="77px"
            onClick={() => handleCommentSubmit()}
          >
            Post
          </Button>
          <IconButton
            data-analytics-id={analyticsIds.insuredProfilePage.activityLog.trashCommentButton}
            icon={<Icon as={BsTrash} boxSize={5} />}
            variant="textGrey"
            onClick={() => handleCommentCancel()}
          />
        </HStack>
      </Box>
    </Flex>
  );
};

ActivityLogAddComment.propTypes = {
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  activityType: PropTypes.string,
  subComment: PropTypes.bool
};

export default ActivityLogAddComment;
